<template>
  <component-wrapper v-if="mountedNonDialog">
    <template v-if="isAppView">
      <registration-fi v-if="!isFite" />
      <registration-fite-fi v-else />
    </template>
    <div
      v-else
      class="page-registration-form"
    >
      <div v-if="isRegistrationLight && isValidateToken">
        <loading-indicator
          variant="spinnerLocal"
          class="registration-form__loading"
        />
      </div>

      <div
        v-else-if="
          (isRegistrationLight && !invitationToken) || (isRegistrationLight && invalidToken)
        "
        class="registration-form__token-error"
      >
        <grid-container-fi>
          <div
            class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-4 lg:fi-col-end-10"
          >
            <div class="registration-form__result-status">
              <result-status-fi :error="invalidTokenError" />
            </div>
          </div>
        </grid-container-fi>
      </div>

      <template v-else>
        <transition leave-active-class="animated fadeOut">
          <div
            v-if="!formSuccess"
            v-intersect="onIntersectModule"
            class="registration-form"
          >
            <template v-if="fields.headline?.value">
              <grid-container-fi>
                <div
                  class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-3 lg:fi-col-end-11"
                >
                  <headline-fi
                    class="registration-form__headline"
                    tag="h1"
                    :size="60"
                  >
                    {{ fields.headline.value }}
                  </headline-fi>
                </div>
              </grid-container-fi>

              <section-divider-small-fi />
            </template>

            <grid-container-fi :container="false">
              <div class="fi-col-span-full">
                <div
                  ref="registrationFormHead"
                  class="registration-form__head"
                  :class="overflowClasses"
                >
                  <ul class="fi-mb-0">
                    <li
                      v-intersect="onOverflowLeft"
                      class="registration-form__detector-left"
                    />
                    <li
                      v-for="(headline, index) in stepHeadlines"
                      :key="index"
                      :class="getStepStateClass(index)"
                      class="registration-form__head-item"
                      @click="changeStep(index)"
                    >
                      <p class="mb-0">
                        <span class="registration-form__head-item-number">
                          {{ index + 1 }}
                        </span>
                        <span class="registration-form__head-item-title">
                          {{ headline }}
                        </span>
                      </p>
                    </li>
                    <li
                      v-intersect="onOverflowRight"
                      class="registration-form__detector-right"
                    />
                  </ul>
                </div>
              </div>
            </grid-container-fi>

            <grid-container-fi>
              <div
                class="fi-col-start-1 fi-col-end-13 sm:fi-col-start-2 sm:fi-col-end-12 lg:fi-col-start-3 lg:fi-col-end-11"
              >
                <form ref="registrationForm">
                  <registration-light
                    v-if="isRegistrationLight"
                    :current-step="currentStep"
                    @valid-changed-step-contact-data="(value) => (stepContactDataValid = value)"
                    @valid-changed-step-password="(value) => (stepPasswordValid = value)"
                  />
                  <registration-without-target-group
                    v-else-if="isRegistrationWithoutTargetGroup"
                    :current-step="currentStep"
                    @step-type-changed="(value) => stepTypeChanged(value)"
                    @valid-changed-step-contact-data="(value) => (stepContactDataValid = value)"
                    @valid-changed-step-address="(value) => (stepAddressValid = value)"
                    @valid-changed-step-password="(value) => (stepPasswordValid = value)"
                  />
                  <registration-default
                    v-else
                    :current-step="currentStep"
                    @step-type-changed="(value) => stepTypeChanged(value)"
                    @valid-changed-step-target-group="(value) => (stepTargetGroupValid = value)"
                    @valid-changed-step-contact-data="(value) => (stepContactDataValid = value)"
                    @valid-changed-step-address="(value) => (stepAddressValid = value)"
                    @valid-changed-step-password="(value) => (stepPasswordValid = value)"
                  />
                </form>
              </div>
            </grid-container-fi>

            <grid-container-fi>
              <div class="fi-col-span-full">
                <div
                  v-intersect="onIntersectControls"
                  class="registration-form__actions"
                >
                  <div
                    class="registration-form__actions-container"
                    :class="
                      stickyContentsEnabled ? 'registration-form__actions-container--sticky' : null
                    "
                  >
                    <div class="registration-form__actions__button-wrapper">
                      <loading-indicator-wrapper
                        :loading="isLoading || false"
                        dark
                        variant="spinnerLocal"
                        spinoff="in-relative-centered"
                        class="registration-form__spinner"
                      >
                        <div class="registration-form__button-wrapper">
                          <button-fi
                            class="button-fi__primary--large"
                            type="submit"
                            :disabled="isNextButtonDisabled"
                            data-test="registration-next-step"
                            @click.prevent.stop="nextStep"
                          >
                            <template v-if="currentStep < maxSteps">
                              {{ fields.buttonNextStep?.value }}
                            </template>
                            <template v-else>
                              {{ fields.buttonSubmit?.value }}
                            </template>
                          </button-fi>
                        </div>
                      </loading-indicator-wrapper>
                    </div>
                  </div>
                </div>
              </div>
            </grid-container-fi>

            <template v-if="Boolean(fields.verifyAddress?.value)">
              <confirm-user-address-dialog-fi
                v-if="googleAddress"
                ref="confirmUserAddressDialog"
                :google-address="googleAddress"
                @keep-address="keepAddress"
              />

              <GMapMap
                v-show="false"
                ref="gmap"
              />
            </template>
          </div>
        </transition>

        <transition enter-active-class="animated fadeIn delay-1s">
          <grid-container-fi v-if="formSuccess">
            <div
              class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-4 lg:fi-col-end-10"
            >
              <div class="registration-form__result-status">
                <result-status-fi :headline="fields.successEmailHasBeenSentBoxTitle">
                  <p
                    v-if="fields.successMessage?.value"
                    class="fi-mb-30"
                  >
                    {{ fields.successMessage.value }}
                  </p>

                  <div class="button-container">
                    <button-fi
                      v-if="hasNecessaryLinkDetails(getLink('homeLink'))"
                      class="button-fi__primary--large button-fi--stretched fi-mb-24 fi-mt-24"
                      type="button"
                      @click="goTo(getLink('homeLink').url || '')"
                    >
                      {{ getLink('homeLink').text }}
                    </button-fi>
                  </div>
                </result-status-fi>
              </div>
            </div>
          </grid-container-fi>
        </transition>
      </template>
    </div>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useClientHandle } from '@urql/vue';
import { ButtonFi, HeadlineFi, LoadingIndicator, LoadingIndicatorWrapper } from 'atoms';
import { useRegistrationStore } from 'store/registration';
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GMapMap from '@/components/google-maps/GMapMap';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import type { ToastAction } from '@/components/notifications-fi/toast-notification-fi/toast-notification-item-fi/ToastNotificationItemFi.vue';
import ConfirmUserAddressDialogFi from '@/components/registration/confirm-user-address-dialog/ConfirmUserAddressDialogFi.vue';
import RegistrationDefault from '@/components/registration/fischer/RegistrationDefault.vue';
import RegistrationLight from '@/components/registration/fischer/RegistrationLight.vue';
import RegistrationWithoutTargetGroup from '@/components/registration/fischer/RegistrationWithoutTargetGroup.vue';
import RegistrationFi from '@/components/registration-fi/RegistrationFi.vue';
import RegistrationFiteFi from '@/components/registration-fi/RegistrationFiteFi.vue';
import ResultStatusFi from '@/components/result-status-fi/ResultStatusFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useConsents } from '@/composables/consents';
import { useEventHub } from '@/composables/eventHub';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import type {
  GetInvitationByTokenQuery,
  GetInvitationByTokenQueryVariables,
  RegisterMutation,
  RegisterMutationVariables,
} from '@/generated/graphql';
import type { GoogleAddress } from '@/googleHelpers';
import GET_INVITATION_BY_TOKEN from '@/graphql/getInvitationByToken.gql';
import REGISTER from '@/graphql/register.gql';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import SectionDividerSmallFi from '@/pages/section-divider-fi/SectionDividerSmallFi.vue';
import { useNotificationStore } from '@/store/pinia/notification';
import { getLinkData, hasNecessaryLinkDetails } from '@/utils/linkHelper';
import { scrollToElement } from '@/utils/pageScrolling';

defineOptions({ name: 'MyAccountRegister' });

const fields = useFields('MyAccountRegisterFields');

const fischer = useFischer();
const { isXS, isSM, isMD } = useBreakpoints();
const registrationStore = useRegistrationStore();
const notificationStore = useNotificationStore();
const { getRegistrationConsentTemplates } = useConsents();
const { sendEvent } = useEventHub();
const handle = useClientHandle();

const confirmUserAddressDialogRef = useTemplateRef('confirmUserAddressDialog');
const registrationFormHeadRef = useTemplateRef('registrationFormHead');

const formSuccess = ref(false);
const registrationFailed = ref(false);
const errorCode = ref<boolean | string>(false);
const isLoading = ref(false);
const currentStep = ref(1);
const currentStepType = ref<string | null>(null);
const classDone = 'registration-form__head-item--done';
const classActive = 'registration-form__head-item--active';
const isRegistrationVisible = ref(false);
const isButtonVisible = ref(false);
const showShadowLeft = ref(false);
const showShadowRight = ref(false);
const stepTargetGroupValid = ref(false);
const stepContactDataValid = ref(false);
const stepAddressValid = ref(false);
const stepPasswordValid = ref(false);
const invitationToken = ref<string | null>(null);
const invalidToken = ref(false);
const isValidateToken = ref(false);
const googleAddress = ref<GoogleAddress | null>(null);
const geoCoder = ref<google.maps.Geocoder | null>(null);
const checkAddress = ref(false);
const mountedNonDialog = ref(false);

const isRegistrationLight = computed(() => fields.registrationType?.value === 'Light');
const isRegistrationWithoutTargetGroup = computed(
  () => fields.registrationType?.value === 'WithoutTargetGroup',
);
const registrationType = computed(() => fields.registrationType?.value || 'Default');

const maxSteps = computed(() => {
  if (isRegistrationLight.value) return 2;
  return isRegistrationWithoutTargetGroup.value ? 3 : 4;
});

const isNextButtonDisabled = computed(() => {
  if (currentStep.value === 1) {
    if (isRegistrationLight.value || isRegistrationWithoutTargetGroup.value) {
      return !stepContactDataValid.value;
    }
    return !stepTargetGroupValid.value;
  }
  if (currentStep.value === 2) {
    if (isRegistrationLight.value) return !stepPasswordValid.value;
    if (isRegistrationWithoutTargetGroup.value) return !stepAddressValid.value;
    return !stepContactDataValid.value;
  }
  if (currentStep.value === 3) {
    if (isRegistrationWithoutTargetGroup.value) return !stepPasswordValid.value;
    return !stepAddressValid.value;
  }
  if (currentStep.value === 4) return !stepPasswordValid.value;
  return true;
});

const stickyContentsEnabled = computed(() => isRegistrationVisible.value && !isButtonVisible.value);

const overflowClasses = computed(() => {
  let classes = '';
  if (showShadowLeft.value) classes += 'registration-form__head--shadow-left ';
  if (showShadowRight.value) classes += 'registration-form__head--shadow-right ';
  return classes;
});

const stepHeadlines = computed(() => {
  if (isRegistrationLight.value) {
    return [fields.headlineStep2?.value || 'Step 2', fields.headlineStep4?.value || 'Step 4'];
  }
  if (isRegistrationWithoutTargetGroup.value) {
    return [
      fields.headlineStep2?.value || 'Step 2',
      fields.headlineStep3?.value || 'Step 3',
      fields.headlineStep4?.value || 'Step 4',
    ];
  }
  return [
    fields.headlineStep1?.value || 'Step 1',
    fields.headlineStep2?.value || 'Step 2',
    fields.headlineStep3?.value || 'Step 3',
    fields.headlineStep4?.value || 'Step 4',
  ];
});

const invalidTokenError = computed(() => ({
  title: fields.invalidTokenTitle?.value,
  message: fields.invalidTokenMessage?.value,
}));

const isDialog = () =>
  !fischer.app.isAppView() && !fischer.query('token') && fischer.isLoginDialogEnabled();

const getStepStateClass = (index: number) => {
  if (index + 1 === currentStep.value) return classActive;
  if (index + 1 <= currentStep.value) return classDone;
  return '';
};

const changeStep = (index: number) => {
  if (getStepStateClass(index) === classDone && index + 1 < currentStep.value) {
    currentStep.value = index + 1;
    registrationStore.setCaptchaResponse('');
  }
};

const goTo = (url: string) => {
  if (url) {
    window.location.href = url;
  }
};

const getLink = (name: string) => (fields ? getLinkData(fields, name) : {});

const sentStepEvent = (value: string) => {
  sendEvent('custom', {}, 'registration_step', {
    kind: registrationType.value,
    step: value,
  });
};

const enterErrorState = (error: unknown) => {
  console.error(error);
  registrationFailed.value = true;
  isLoading.value = false;

  if (typeof errorCode.value === 'string' && errorCode.value === 'DuplicateUidError') {
    let action: ToastAction | undefined;

    if (
      fields.errorAlreadyRegisteredMessageLink?.value &&
      (fields.errorAlreadyRegisteredMessageLink.value.url ||
        fields.errorAlreadyRegisteredMessageLink.value.href) &&
      fields.errorAlreadyRegisteredMessageLink.value.text
    ) {
      action = {
        label: fields.errorAlreadyRegisteredMessageLink.value.text,
        clicked: () =>
          goTo(
            fields.errorAlreadyRegisteredMessageLink.value.url ||
              fields.errorAlreadyRegisteredMessageLink.value.href,
          ),
        closeOnClick: true,
      };
    }

    notificationStore.openToastNotification({
      message: { value: fields.errorAlreadyRegisteredMessage?.value },
      type: 'error',
      icon: 'alert-triangle',
      persist: true,
      close: true,
      action,
    });
  } else {
    notificationStore.openGeneralErrorNotification();
  }
};

const submitForm = async () => {
  if (
    (isRegistrationWithoutTargetGroup.value || isRegistrationLight.value
      ? false
      : !stepTargetGroupValid.value) ||
    !stepContactDataValid.value ||
    (isRegistrationLight.value ? false : !stepAddressValid.value) ||
    !stepPasswordValid.value
  ) {
    console.error('form not valid');
    return;
  }

  isLoading.value = true;
  registrationFailed.value = false;

  try {
    const consentTemplates = await getRegistrationConsentTemplates();
    const { data, error: mutError } = await handle.client
      .mutation<RegisterMutation, RegisterMutationVariables>(REGISTER, {
        baseSiteId: fischer.baseSiteId(),
        host: fischer.hostname(),
        targetGroup: registrationStore.targetGroup,
        salutation: registrationStore.salutation?.[0] || '',
        title: registrationStore.title,
        firstName: registrationStore.firstname,
        lastName: registrationStore.lastname,
        email: registrationStore.email,
        phoneNumber: registrationStore.phone,
        subTargetGroup: registrationStore.subTargetGroup,
        country: registrationStore.country,
        companyName: registrationStore.company,
        customerNumber: registrationStore.customerNumber,
        streetName: registrationStore.street,
        streetNumber: registrationStore.streetNumber,
        zipCode: registrationStore.zipcode,
        city: registrationStore.city,
        password: registrationStore.password,
        passwordValidation: registrationStore.passwordRepeat,
        consentTemplates,
        captchaResponse: registrationStore.captchaResponse,
        lang: fischer.language_639_1(),
        invitationToken: invitationToken.value,
      })
      .toPromise();
    if (mutError) {
      enterErrorState(mutError);
      return;
    }

    const { statusCode, errorCode: respErrorCode } = data?.register || {
      errorCode: 'did not get registration data',
    };
    const isSuccessCase = statusCode === 201 && !respErrorCode;

    if (isSuccessCase) {
      const layerData: { event: string; UserGroup: any; UserCraft?: string } = {
        event: 'myfischerRegistration',
        UserGroup: registrationStore.targetGroupTitle,
      };

      if (
        registrationStore.targetGroup === 'GROUP-craftsman' &&
        registrationStore.subTargetGroupTitle
      ) {
        layerData.UserCraft = registrationStore.subTargetGroupTitle;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(layerData);
      sendEvent('custom', {}, 'registration_success', {
        kind: registrationType.value,
        step: fields.headlineStep4?.value,
      });
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });

    isLoading.value = false;
    formSuccess.value = isSuccessCase;
    if (!isSuccessCase) {
      errorCode.value = respErrorCode || 'unknown';
      enterErrorState(respErrorCode);
    }
  } catch (error) {
    enterErrorState(error);
  }
};

const scrollMobileTabs = () => {
  if (isXS.value || (isSM.value && !isMD.value)) {
    setTimeout(() => {
      const head = registrationFormHeadRef.value;
      const targetTab = head?.querySelector<HTMLElement>('.registration-form__head-item--active');
      if (!head || !targetTab) {
        console.error('Could not scroll find tab to scroll to', head, targetTab);
        return;
      }
      const targetTabPosition =
        head.clientWidth - (targetTab.offsetLeft + targetTab.offsetWidth) + 4;

      if (targetTabPosition < 0) {
        head.scrollLeft = targetTabPosition * -1;
      }
    }, 20);
  }
};

const verifyAddress = async () => {
  try {
    const userAddress = `${registrationStore.street} ${registrationStore.streetNumber}, ${registrationStore.zipcode} ${registrationStore.city}`;

    if (!geoCoder.value) {
      geoCoder.value = new google.maps.Geocoder();
    }

    const geocodeVars: google.maps.GeocoderRequest = { address: userAddress };
    if (registrationStore.country) {
      geocodeVars.componentRestrictions = { country: registrationStore.country };
    }

    geoCoder.value.geocode(geocodeVars, async (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results) {
        const { address_components: addressComponents } = results[0];
        const streetNumber =
          addressComponents.find((el) => el.types.includes('street_number'))?.long_name || null;
        const street =
          addressComponents.find((el) => el.types.includes('route'))?.long_name || null;
        const town =
          addressComponents.find((el) => el.types.includes('sublocality'))?.long_name || null;
        const city =
          addressComponents.find((el) => el.types.includes('locality'))?.long_name || null;
        const zipcode =
          addressComponents.find((el) => el.types.includes('postal_code'))?.long_name || null;

        let googleCity = city;
        if (town === registrationStore.city) {
          googleCity = town;
        }

        googleAddress.value = {
          streetNumber,
          street,
          city: googleCity || null,
          zipcode,
          country: registrationStore.country || null,
        };

        if (
          !(streetNumber && street && googleCity && zipcode) ||
          (streetNumber === registrationStore.streetNumber &&
            street === registrationStore.street &&
            googleCity === registrationStore.city &&
            zipcode === registrationStore.zipcode)
        ) {
          currentStep.value += 1;
          scrollMobileTabs();
          scrollToElement(registrationFormHeadRef.value, 'center');
        } else {
          await nextTick();
          confirmUserAddressDialogRef.value?.open();
        }
      }
    });
  } catch (error) {
    console.error(`Error getting LatLon coords: ${error}`);
  }
};

const nextStep = () => {
  if (currentStep.value < maxSteps.value) {
    if (currentStepType.value === 'address' && checkAddress.value) {
      verifyAddress();
    } else {
      currentStep.value += 1;
      scrollMobileTabs();
      scrollToElement(registrationFormHeadRef.value, 'center');
      checkAddress.value = Boolean(fields.verifyAddress?.value);
    }
  } else {
    submitForm();
  }
};

const keepAddress = () => {
  checkAddress.value = false;
  googleAddress.value = null;
  nextStep();
};

const stepTypeChanged = (value: string) => {
  currentStepType.value = value;
  sentStepEvent(value);
};

const onIntersectModule = (observer: any) => {
  isRegistrationVisible.value =
    observer[0].isIntersecting && observer[0].boundingClientRect.top > 0;
};

const onIntersectControls = (observer: any) => {
  isButtonVisible.value = observer[0].isIntersecting && observer[0].boundingClientRect.top > 0;
};

const onOverflowLeft = (_entries: any, _observer: any, isIntersecting: boolean) => {
  showShadowLeft.value = !isIntersecting;
};

const onOverflowRight = (_entries: any, _observer: any, isIntersecting: boolean) => {
  showShadowRight.value = !isIntersecting;
};

const validateToken = async (token: string) => {
  isValidateToken.value = true;
  try {
    const { data: invitationData, error: queryError } = await handle.client
      .query<
        GetInvitationByTokenQuery,
        GetInvitationByTokenQueryVariables
      >(GET_INVITATION_BY_TOKEN, { baseSiteId: fischer.baseSiteId(), invitationToken: token }, { requestPolicy: 'network-only' })
      .toPromise();
    if (queryError) {
      invalidToken.value = true;
      isValidateToken.value = false;
      return;
    }
    registrationStore.email = invitationData?.getInvitationByToken?.invitationEmail || '';
    invitationToken.value = token;
    isValidateToken.value = false;
  } catch (_error) {
    invalidToken.value = true;
    isValidateToken.value = false;
  }
};

// created logic
const initStep =
  isRegistrationLight.value || isRegistrationWithoutTargetGroup.value
    ? 'contact-data'
    : 'target-group';

if (!IS_SSR && !isDialog()) {
  checkAddress.value = Boolean(fields.verifyAddress?.value);

  const tokenParam = fischer.query('token');
  if (isRegistrationLight.value && tokenParam) {
    validateToken(tokenParam as string);
  }

  sentStepEvent(initStep);
}

onMounted(() => {
  if (isDialog() && (!IS_SHOWROOM || window.confirm('redirect to login dialog?'))) {
    const page: keyof FischerPlugin['pages'] = 'registration';
    const dialogToken = `${page}-dialog`;

    // Carry the incoming hash across the hop into the dialog: it now holds the
    // FiXperience offline migration link's id/validation/date/locale params (see
    // fixperienceMigration.ts), and without them the dialog opens with nothing to
    // prefill from. The dialog token must come first, since LoginDialogHandlerFi's
    // `(?:^#|&)${dialog}-dialog(?:$|&)` match expects it at the front. A
    // cross-locale redirect carries the whole hash over (buildLocaleRedirectUrl),
    // so the token can already be present here - strip it before re-adding so it
    // is not duplicated. When there is nothing to carry this is byte-identical to
    // the old URL.
    const incomingHash = window.location.hash
      .replace(/^#/, '')
      .split('&')
      .filter((token) => token !== '' && token !== dialogToken)
      .join('&');

    // Keep carrying window.location.search too: it may hold unrelated params, and
    // dropping them would be a regression. It already includes its own leading
    // '?' (or is '' when there is none).
    window.location.href =
      `${fischer.hostUrlBase()}/${fischer.siteLanguage().toLowerCase()}` +
      window.location.search +
      `#${dialogToken}${incomingHash ? `&${incomingHash}` : ''}`;
  } else {
    mountedNonDialog.value = true;
  }
});
</script>

<style src="./my-account-registration" />
